// The onboarding flow as data: which steps a patient walks through, in which
// order. ONE place decides it — App renders `steps[currentStep - 1]`, the
// progress bar counts `steps.length`, analytics reads `step.label`. Before
// this, App.tsx encoded the order three times (render switch, step labels,
// total count) with index arithmetic that had to agree.
//
// A cohort variant (e.g. psychotherapy) is a different list built here, not a
// branch inside the step components.
//
// Invariants:
//  - Labels are an API: FlowEvent → AnalyticsDaily → admin analytics key on
//    `step{n}_{suffix}`. src/flow/__tests__/flow.test.ts pins them for every
//    org × payment step × test mode combination.
//  - The patient's position is persisted as a 1-based index (currentStep) —
//    a deploy must not move patients who are mid-flow.
import type { ContractConfig, PaymentStep } from '../context/TenantContext'

export type StepKind =
  | 'welcome' | 'personal' | 'address' | 'insurance' | 'upload' | 'summary'
  | 'contract' | 'phone_otp' | 'sepa' | 'card_payment' | 'complete'
  // psychotherapy variant (docs/psychotherapy-variant.md)
  | 'pt_details' | 'pt_payment'

export type Cohort = 'standard' | 'psychotherapy' | 'psychotherapy_returning'

export interface FlowStep {
  kind: StepKind
  /** Analytics suffix: the label is `step{n}_{suffix}` */
  suffix: string
  /** For contract steps: the contract shown */
  contract?: ContractConfig
}

export interface FlowContext {
  country?: string | null
  contracts: ContractConfig[]
  paymentStep: PaymentStep
  testMode: boolean
  /** ?nosepa=1 links for patients who already have a mandate */
  skipSepaOverride: boolean
}

export function showsSepaStep(ctx: Pick<FlowContext, 'paymentStep' | 'testMode' | 'skipSepaOverride'>): boolean {
  return (ctx.paymentStep === 'sepa' || (ctx.paymentStep === 'sepa_test' && ctx.testMode)) && !ctx.skipSepaOverride
}

export function showsCardStep(ctx: Pick<FlowContext, 'paymentStep' | 'testMode'>): boolean {
  return ctx.paymentStep === 'card' || (ctx.paymentStep === 'card_test' && ctx.testMode)
}

/** The standard onboarding (every organisation today). */
export function standardFlow(ctx: FlowContext): FlowStep[] {
  const steps: FlowStep[] = [
    { kind: 'welcome', suffix: 'welcome' },
    { kind: 'personal', suffix: 'personal' },
    { kind: 'address', suffix: 'address' },
    // Italy asks about the legal guardian in this slot — the funnel must not call that "Versicherung"
    { kind: 'insurance', suffix: ctx.country === 'IT' ? 'guardian' : 'insurance' },
    { kind: 'upload', suffix: 'upload' },
    { kind: 'summary', suffix: 'summary' },
    ...ctx.contracts.map(c => ({ kind: 'contract' as const, suffix: `contract_${c.key}`, contract: c })),
  ]
  if (ctx.contracts.length > 0) steps.push({ kind: 'phone_otp', suffix: 'phone_otp' })
  if (showsSepaStep(ctx)) steps.push({ kind: 'sepa', suffix: 'sepa' })
  if (showsCardStep(ctx)) steps.push({ kind: 'card_payment', suffix: 'card_payment' })
  steps.push({ kind: 'complete', suffix: 'complete' })
  return steps
}

export interface PsychotherapyContext {
  /** The contract steps in signing order, already resolved (src/flow/ptContracts.ts) */
  contracts: ContractConfig[]
  /** Returning patients who set up a SEPA mandate before get no payment step */
  hasMandate?: boolean
  /** The insurance step's answer: new private / self-paying patients have no details to give */
  insuranceType?: string
}

/**
 * New psychotherapy patient: the full onboarding plus the psychotherapy details (statutory insured
 * patients: the questions of the insurer application and the two choices of Dok. 02) and the
 * Probatorik payment, which replaces the org's SEPA / card step.
 *
 * Private and self-paying patients have no details to give since the setting question went
 * (Paul, 2026-10-29: GAM only offers online sessions), so they skip the page. Safe for the
 * persisted position: the insurance type is answered on the step right before it.
 */
export function psychotherapyFlow(ctx: FlowContext, pt: PsychotherapyContext): FlowStep[] {
  const details = pt.insuranceType !== 'private' && pt.insuranceType !== 'none'
  return [
    { kind: 'welcome', suffix: 'welcome' },
    { kind: 'personal', suffix: 'personal' },
    { kind: 'address', suffix: 'address' },
    { kind: 'insurance', suffix: ctx.country === 'IT' ? 'guardian' : 'insurance' },
    ...(details ? [{ kind: 'pt_details' as const, suffix: 'pt_details' }] : []),
    { kind: 'upload', suffix: 'upload' },
    { kind: 'summary', suffix: 'summary' },
    ...pt.contracts.map(c => ({ kind: 'contract' as const, suffix: `contract_${c.key}`, contract: c })),
    { kind: 'phone_otp', suffix: 'phone_otp' },
    { kind: 'pt_payment', suffix: 'pt_payment' },
    { kind: 'complete', suffix: 'complete' },
  ]
}

/**
 * Did the onboarding before, now accepted for psychotherapy: ONLY the new parts (Paul,
 * 2026-10-28) — no second full form, nothing signed twice. The summary shows the data the
 * documents are signed with (from the earlier submission).
 */
export function psychotherapyReturningFlow(pt: PsychotherapyContext): FlowStep[] {
  const steps: FlowStep[] = [
    { kind: 'welcome', suffix: 'welcome' },
    { kind: 'pt_details', suffix: 'pt_details' },
    { kind: 'summary', suffix: 'summary' },
    ...pt.contracts.map(c => ({ kind: 'contract' as const, suffix: `contract_${c.key}`, contract: c })),
    { kind: 'phone_otp', suffix: 'phone_otp' },
  ]
  if (!pt.hasMandate) steps.push({ kind: 'pt_payment', suffix: 'pt_payment' })
  steps.push({ kind: 'complete', suffix: 'complete' })
  return steps
}

/** The flow of a cohort. Psychotherapy without its contract list falls back to the standard flow. */
export function flowFor(cohort: Cohort, ctx: FlowContext, pt: PsychotherapyContext | null): FlowStep[] {
  if (cohort === 'psychotherapy' && pt && pt.contracts.length > 0) return psychotherapyFlow(ctx, pt)
  if (cohort === 'psychotherapy_returning' && pt && pt.contracts.length > 0) return psychotherapyReturningFlow(pt)
  return standardFlow(ctx)
}

/** `step{n}_{suffix}` for the 1-based position — `step{n}` past the end (never happens in a clamped flow). */
export function stepLabel(steps: FlowStep[], position: number): string {
  const s = steps[position - 1]
  return s ? `step${position}_${s.suffix}` : `step${position}`
}
